import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraft } from "@/hooks/use-draft";
import {
  AlertTriangle,
  ArrowUp,
  AtSign,
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Clock,
  Eye,
  FolderGit2,
  ImagePlus,
  Loader2,
  Network,
  Pencil,
  Sparkles,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
// Title and description are Radix Dialog parts, so they serve the sheet unchanged.
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import { Input, Textarea } from "@/components/ui/input";
import { ComposerTextarea, isSendKey, SEND_BUTTON_CLASS, SendHint } from "@/components/composer-textarea";
import { mentionCandidates } from "@/components/mention-autocomplete";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, Separator } from "@/components/ui/misc";
import { avatarOf } from "@/lib/avatars";
import { useForeignPresence } from "@/hooks/use-foreign-presence";
import { boardProjectIds } from "@/lib/board-projects";
import { Badge } from "@/components/ui/badge";
import { Hint } from "@/components/ui/tooltip";
import { Markdown } from "@/components/markdown";
import { agentHandles, agentIdsByHandle, hasAgentMention, mentionedAgentHandles } from "@/components/mention-text";
import { ProjectSelect, shortPath } from "@/components/project-select";
import { AreaChips, AreasEditor } from "@/components/areas-field";
import { RunStopButton } from "@/components/run-stop-button";
import { ResponseBoxes } from "@/components/response-boxes";
import { PhotoCarousel } from "@/components/photo-carousel";
import { ResponsePanel } from "@/components/response-panel";
import { StopAgentButton } from "@/components/stop-agent-button";
import { ImageGroup } from "@/components/image-viewer";
import { WaitingBlock } from "@/components/waiting-block";
import { QuickStateButton, StateBadgeTrigger, StateMenu } from "@/components/card-state";
import { useQuickMove } from "@/hooks/use-quick-move";
import { useWaiting } from "@/lib/waiting-context";
import { api, ApiError } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query/keys";
import { applyTaskPatch, type BoardsData, type TaskFieldPatch } from "@/lib/query/optimistic";
import { useCan } from "@/lib/auth";
import { toggleTaskListItem } from "@/lib/task-list";
import { humanBytes, toBase64 } from "@/lib/files";
import { formatDateTime, relativeTime, toLocalInputValue, fromLocalInputValue } from "@/lib/format";
import {
  kindColor,
  MENTION_STATUS_LABELS,
  priorityColor,
  PRIORITY_LABELS,
  type BoardDetail,
  type MentionStatus,
  type MentionWithContext,
  type CommentKind,
  type Priority,
  type Project,
  type TaskComment,
  type TaskPhoto,
  type TaskResponseSummary,
  type User,
  type AgentWithContext,
  type CardCanvasRef,
  type TaskDetail,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];
const UNASSIGNED = "__unassigned__";

/** Open requests read as live; resolved ones recede into the thread. */
const MENTION_TINT: Record<MentionStatus, string> = {
  pending: "var(--kind-review)",
  claimed: "var(--primary)",
  answered: "var(--kind-done)",
  dismissed: "var(--muted-foreground)",
};

/**
 * How each kind of comment reads in the thread.
 *
 * `note` gets no chrome at all — most of the thread is notes, and a badge on
 * every one of them is a badge on none of them. The other three are Claude
 * narrating a job, and `blocker` is the one the whole treatment exists for: it
 * is the only comment in a thread that is asking the user to do something.
 */
const COMMENT_KIND: Record<Exclude<CommentKind, "note">, { label: string; tint: string; icon: typeof Check }> = {
  progress: { label: "Progress", tint: "var(--kind-active)", icon: CircleDashed },
  blocker: { label: "Blocked", tint: "var(--kind-blocked)", icon: AlertTriangle },
  result: { label: "Result", tint: "var(--kind-done)", icon: Check },
};

export interface TaskDialogProps {
  taskId: string | null;
  boards: BoardDetail[];
  users: User[];
  /** Registered directories, for the picker and for naming the inherited one. */
  projects: Project[];
  /** For "working on this card" and its Stop button. */
  agents?: AgentWithContext[];
  onClose: () => void;
  onChanged: () => void;
  onError: (message: string) => void;
  /** An agent's chat at one message: a question on this card, in the chat it was asked in. */
  onOpenAgent?: (agentId: string, messageId: string) => void;
  /** Opens a board — the canvases this card is a node on are links to theirs. */
  onOpenBoard?: (boardId: string) => void;
}

/**
 * Detail view and editor for one card. It opens **read-only** so the card can be
 * read without risk of nudging a field; the pencil in the top-left corner unlocks
 * the form. Once editing, field edits save on blur (or on select) rather than
 * behind a Save button, so the board and the agent see changes immediately. The
 * comment box stays live in both modes — it is the hand-off channel to Claude,
 * not an edit to the card.
 */
const selectComments = (data: { comments: TaskComment[] }) => data.comments;
const selectMentions = (data: { mentions: MentionWithContext[] }) => data.mentions;
const selectPhotos = (data: { photos: TaskPhoto[] }) => data.photos;
const selectCanvases = (data: TaskDetail) => data.canvases ?? NO_CANVASES;
const NO_CANVASES: CardCanvasRef[] = [];
const NO_COMMENTS: TaskComment[] = [];
const NO_MENTIONS: MentionWithContext[] = [];
const NO_PHOTOS: TaskPhoto[] = [];
const NO_RESPONSES: TaskResponseSummary = { responses: [], activeDraftTurn: null };

/** Kept in step with core's limits, so a refusal happens before the upload. */
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

interface StagedImage {
  key: string;
  file: File;
  /** Object URL, revoked when the image is unstaged or sent. */
  preview: string;
}

export function TaskDialog({ taskId, boards, users, projects, agents = [], onClose, onChanged, onError, onOpenAgent, onOpenBoard }: TaskDialogProps) {
  const board = boards.find((entry) => entry.tasks.some((task) => task.id === taskId));
  const task = board?.tasks.find((entry) => entry.id === taskId) ?? null;
  const column = board?.columns.find((entry) => entry.id === task?.columnId) ?? null;
  /** Foreign agents (on someone's own machine) get a corner mark on their avatar. */
  const foreign = useForeignPresence();
  // Stable across refetches: the joined ids, not the array a refetch hands out anew.
  const boardIdsKey = boardProjectIds(board).join(",");
  const boardIds = useMemo(() => (boardIdsKey ? boardIdsKey.split(",") : []), [boardIdsKey]);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  /** The description's own editor: the raw text, open only between Edit and Save/Cancel. */
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  /**
   * The text a checkbox tick has written but the board has not yet re-read. The
   * box would otherwise spring back until the refetch lands, and a second tick
   * in that gap would start from the stale text and undo the first.
   */
  const [tickedDescription, setTickedDescription] = useState<string | null>(null);
  /** Viewers read a description; the server refuses them a write anyway. */
  const canEdit = useCan("member");
  /**
   * The thread, from the cache and re-read whenever the live feed says a write
   * touched comments or mentions (the `task` family), not on `task.updatedAt`:
   * posting a comment does not touch the task row, so keying it off the card's
   * own timestamp meant a run's progress comments did not appear in a dialog the
   * user had open — which is exactly the dialog they have open while it works.
   */
  const client = useQueryClient();
  const commentsQuery = useQuery({
    queryKey: qk.taskComments(taskId ?? ""),
    queryFn: () => api.comments(taskId!),
    enabled: taskId !== null,
    select: selectComments,
  });
  const comments = (taskId && commentsQuery.data) || NO_COMMENTS;
  const setComments = (update: (current: TaskComment[]) => TaskComment[]) => {
    if (!taskId) return;
    client.setQueryData<{ comments: TaskComment[] }>(qk.taskComments(taskId), (data) =>
      data ? { ...data, comments: update(data.comments) } : data,
    );
  };
  const mentionsQuery = useQuery({
    queryKey: qk.taskMentions(taskId ?? ""),
    queryFn: () => api.mentions(taskId!),
    enabled: taskId !== null,
    select: selectMentions,
  });
  const mentions = (taskId && mentionsQuery.data) || NO_MENTIONS;
  const [draft, setDraft] = useDraft(taskId ? `task:${taskId}` : null);
  const [posting, setPosting] = useState(false);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  /** The composer shows the draft rendered instead of the box, as the thread will. */
  const [previewing, setPreviewing] = useState(false);
  /** Set when leaving preview, so the box that comes back takes the focus. */
  const refocusDraft = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (previewing || !refocusDraft.current) return;
    const then = refocusDraft.current;
    refocusDraft.current = null;
    then();
  }, [previewing]);
  const editDraft = (then?: () => void) => {
    refocusDraft.current = () => {
      draftRef.current?.focus();
      then?.();
    };
    setPreviewing(false);
  };
  /** The sheet's scrolling body. The thread lives in it rather than in a box of its own. */
  const threadRef = useRef<HTMLDivElement>(null);
  /** Was the thread scrolled to the bottom before this render? */
  const threadPinnedRef = useRef(false);
  /** The card's own title, so the top bar can name the card once it has scrolled away. */
  const titleRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [titleGone, setTitleGone] = useState(false);
  const [images, setImages] = useState<StagedImage[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);

  const [openResponseId, setOpenResponseId] = useState<string | null>(null);
  const [draftingReplies, setDraftingReplies] = useState(false);

  const handles = useMemo(() => agentHandles(users), [users]);
  const waiting = useWaiting();
  const waitingCard = taskId ? waiting.byTask.get(taskId) : undefined;
  /** Which comments asked Claude for something, and where each ask got to. */
  const mentionByComment = useMemo(
    () => new Map(mentions.map((mention) => [mention.commentId, mention])),
    [mentions],
  );
  const openMentions = useMemo(
    () => mentions.filter((mention) => mention.status === "pending" || mention.status === "claimed"),
    [mentions],
  );
  const draftAsksClaude = hasAgentMention(draft, handles);
  /** Who `@` offers here: the card's assignee first, then agents on its project. */
  const mentionOptions = useMemo(
    () =>
      mentionCandidates(agents, {
        assigneeId: task?.assigneeId ?? null,
        projectId: task?.projectId ?? board?.board.projectId ?? null,
        // A card with no project of its own, on a board covering several, is about any of them.
        projectIds: task?.projectId ? [] : boardIds,
      }),
    [agents, task?.assigneeId, task?.projectId, board?.board.projectId, boardIds],
  );
  /** Handle → the name to say it by, for the "this asks X" line under the box. */
  const agentNameByHandle = useMemo(() => {
    const names = new Map<string, string>();
    for (const [alias, id] of agentIdsByHandle(users)) {
      names.set(alias, users.find((user) => user.id === id)?.displayName ?? id);
    }
    return names;
  }, [users]);
  const draftTargets = useMemo(
    () => [...new Set(mentionedAgentHandles(draft, handles).map((handle) => agentNameByHandle.get(handle) ?? `@${handle}`))],
    [draft, handles, agentNameByHandle],
  );
  const nameOf = (userId: string) => users.find((user) => user.id === userId)?.displayName ?? userId;
  /** "Waiting for Board x", not "Waiting for Claude", when the ask was to Board x. */
  const askLabel = (ask: { status: MentionWithContext["status"]; targetId: string }) =>
    ask.status === "pending"
      ? `Waiting for ${nameOf(ask.targetId)}`
      : ask.status === "claimed"
        ? `${nameOf(ask.targetId)} is on it`
        : MENTION_STATUS_LABELS[ask.status];

  /** Every card opens read-only, including the next one opened without closing the dialog. */
  useEffect(() => {
    setEditing(false);
    setEditingDescription(false);
    setTickedDescription(null);
    setOpenResponseId(null);
    // A card opens at its top — the details — not scrolled to the end of its
    // thread. Following new comments starts once the user scrolls down to them.
    threadPinnedRef.current = false;
    threadRef.current?.scrollTo({ top: 0 });
    setScrolled(false);
    setTitleGone(false);
    setImages((current) => {
      for (const entry of current) URL.revokeObjectURL(entry.preview);
      return [];
    });
  }, [taskId]);

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
  }, [task?.id, task?.title]);

  // Whatever the board now holds supersedes a tick still in flight.
  useEffect(() => setTickedDescription(null), [task?.description]);


  /**
   * Photos from the chat the card came out of. Invalidated with the card's other
   * parts, because linking a photo does not touch the task row. Only a card with a
   * sourceRef can have any, so nothing else pays for the request.
   */
  const hasPhotos = Boolean(taskId && task?.sourceRef?.startsWith("whatsapp:"));
  const photosQuery = useQuery({
    queryKey: qk.taskPhotos(taskId ?? ""),
    queryFn: () => api.taskPhotos(taskId!),
    enabled: hasPhotos,
    select: selectPhotos,
  });
  const photos = (hasPhotos && photosQuery.data) || NO_PHOTOS;
  /** The canvas boards this card is a node on; a link adds no row to the card, so it is read with its detail. */
  const canvasesQuery = useQuery({
    queryKey: qk.taskCanvases(taskId ?? ""),
    queryFn: () => api.getTask(taskId!),
    enabled: taskId !== null,
    select: selectCanvases,
  });
  const canvases = (taskId && canvasesQuery.data) || NO_CANVASES;

  /**
   * Keeps the newest comment in view as a run narrates, but only when the user is
   * already at the bottom — yanking the thread down while they are reading back
   * through what happened is worse than making them scroll.
   *
   * Whether they were at the bottom is recorded as they scroll rather than
   * measured here: by the time this runs the new comment is already laid out, so
   * the distance to the bottom is the height of the thing that just arrived.
   */
  useEffect(() => {
    const thread = threadRef.current;
    if (thread && threadPinnedRef.current) thread.scrollTop = thread.scrollHeight;
  }, [comments]);

  /**
   * The card's draft replies, re-read on every remote change rather than on the
   * card's own timestamp: rewriting a reply does not touch the task row, so
   * keying this off `task.updatedAt` would leave the panel showing the old words.
   */
  const responsesQuery = useQuery({
    queryKey: qk.taskResponses(taskId ?? ""),
    queryFn: () => api.taskResponses(taskId!),
    enabled: taskId !== null,
  });
  // A failed refetch keeps the panel being read; the feed retries.
  const responses = (taskId && responsesQuery.data) || NO_RESPONSES;
  const reloadResponses = useCallback(async () => {
    if (taskId) await client.invalidateQueries({ queryKey: qk.taskResponses(taskId) });
  }, [client, taskId]);

  const openResponse = useMemo(
    () => responses.responses.find((entry) => entry.id === openResponseId) ?? null,
    [responses.responses, openResponseId],
  );

  /** Queues a drafting pass. Nothing is written yet when this resolves. */
  const requestDrafts = async () => {
    if (!taskId) return;
    setDraftingReplies(true);
    try {
      const { alreadyQueued } = await api.requestDrafts(taskId);
      await reloadResponses();
      onChanged();
      if (alreadyQueued) onError("A drafting pass for this card is already queued.");
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not ask for the replies");
    } finally {
      setDraftingReplies(false);
    }
  };

  const cancelDrafts = async () => {
    if (!taskId) return;
    try {
      await api.cancelDrafts(taskId);
      await reloadResponses();
      onChanged();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not cancel that");
    }
  };

  /**
   * A field edit shows at once: written into the cached boards before the request
   * goes, the snapshot put back if the server refuses, and refetched on settle
   * (batched with the revision event its commit publishes).
   */
  const patchTask = useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaskFieldPatch }) => api.updateTask(id, body),
    onMutate: async ({ id, body }) => {
      await client.cancelQueries({ queryKey: qk.boards() });
      const previous = client.getQueryData<BoardsData>(qk.boards());
      client.setQueryData<BoardsData>(qk.boards(), (data) => applyTaskPatch(data, id, body));
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) client.setQueryData(qk.boards(), context.previous);
      onError(error instanceof ApiError ? error.message : "Could not save that change");
    },
    onSettled: () => onChanged(),
  });
  const patch = async (body: TaskFieldPatch) => {
    if (!taskId) return;
    await patchTask.mutateAsync({ id: taskId, body }).catch(() => undefined);
  };

  /** Opens the raw text from what is stored now, not from whatever was typed and cancelled last time. */
  const startDescriptionEdit = () => {
    setDescription(tickedDescription ?? task?.description ?? "");
    setEditingDescription(true);
  };

  /** Stores the text as typed; the editor stays open if the save is refused. */
  const saveDescription = async () => {
    if (!taskId || !task) return;
    if (description === (task.description ?? "")) {
      setEditingDescription(false);
      return;
    }
    setSavingDescription(true);
    try {
      // An empty string, not undefined: undefined drops the key, and clearing a
      // description would then silently keep the old one.
      await api.updateTask(taskId, { description });
      setEditingDescription(false);
      onChanged();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not save the description");
    } finally {
      setSavingDescription(false);
    }
  };

  /** Ticks one `- [ ]` box: that line's `[ ]`↔`[x]` and nothing else, through the ordinary update. */
  const toggleCheckbox = async (index: number) => {
    if (!taskId || !task?.description) return;
    const current = tickedDescription ?? task.description;
    const next = toggleTaskListItem(current, index);
    if (next === current) return;
    setTickedDescription(next);
    try {
      await api.updateTask(taskId, { description: next });
      onChanged();
    } catch (error) {
      setTickedDescription(null);
      onError(error instanceof ApiError ? error.message : "Could not update the checklist");
    }
  };

  const quickMove = useQuickMove();
  const [quickMoving, setQuickMoving] = useState(false);
  /** The bar's Mark done / Reopen: one tap, with Undo in the toast rather than a confirm. */
  const quickStateMove = async (targetId: string, action: "done" | "reopen") => {
    if (!task || quickMoving) return;
    setQuickMoving(true);
    await quickMove({
      taskId: task.id,
      title: task.title,
      fromColumnId: task.columnId,
      toColumnId: targetId,
      verb: action === "done" ? "Done" : "Reopened",
    });
    setQuickMoving(false);
  };

  const move = async (columnId: string) => {
    if (!taskId) return;
    try {
      await api.moveTask(taskId, { column: columnId, force: true });
      onChanged();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not move the task");
    }
  };

  /** Stages images for the next comment; anything but a readable image is refused here. */
  const addImages = (incoming: FileList | File[] | null | undefined) => {
    const files = [...(incoming ?? [])];
    if (files.length === 0) return;
    const accepted: StagedImage[] = [];
    for (const file of files) {
      if (!IMAGE_TYPES.includes(file.type)) {
        onError(`${file.name || "That file"} is not an image the thread can hold — PNG, JPEG, GIF or WebP.`);
        continue;
      }
      if (file.size === 0) {
        onError(`${file.name || "That image"} is empty.`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        onError(`${file.name || "That image"} is ${humanBytes(file.size)}; the limit is 10 MB.`);
        continue;
      }
      accepted.push({ key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`, file, preview: URL.createObjectURL(file) });
    }
    setImages((current) => {
      const next = [...current, ...accepted];
      if (next.length > MAX_IMAGES) {
        onError(`Only ${MAX_IMAGES} images per comment — the rest were dropped.`);
        for (const entry of next.slice(MAX_IMAGES)) URL.revokeObjectURL(entry.preview);
      }
      return next.slice(0, MAX_IMAGES);
    });
  };

  const unstageImage = (key: string) =>
    setImages((current) =>
      current.filter((entry) => {
        if (entry.key === key) URL.revokeObjectURL(entry.preview);
        return entry.key !== key;
      }),
    );

  const postComment = async () => {
    if (!taskId || (!draft.trim() && images.length === 0)) return;
    setPosting(true);
    try {
      const encoded = await Promise.all(
        images.map(async (entry) => ({
          filename: entry.file.name || "pasted-image",
          mime: entry.file.type || undefined,
          data: await toBase64(entry.file),
        })),
      );
      const comment = await api.addComment(taskId, draft.trim(), encoded);
      // Posting is the user joining the conversation, so follow it from here.
      threadPinnedRef.current = true;
      setComments((current) => [...current, comment]);
      setDraft("");
      setPreviewing(false);
      for (const entry of images) URL.revokeObjectURL(entry.preview);
      setImages([]);
      // The POST reports the requests it raised, but not their joined context, so
      // re-read the thread's mentions rather than guessing at the shape.
      if (comment.mentions.length > 0) {
        await client.invalidateQueries({ queryKey: qk.taskMentions(taskId) }).catch(() => undefined);
      }
      onChanged();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not post the comment");
    } finally {
      setPosting(false);
    }
  };

  /** Types an `@` at the caret, which opens the agent picker. */
  const startMention = () => {
    // From preview, go back to the box first; the caret is where it was left.
    if (previewing) editDraft(insertMention);
    else insertMention();
  };
  // Reads only the ref and the setter, so it is safe to run from the render after.
  const insertMention = () => {
    const field = draftRef.current;
    if (!field) return;
    field.focus();
    const at = field.selectionStart ?? field.value.length;
    const before = field.value.slice(0, at);
    const text = before && !/\s$/.test(before) ? " @" : "@";
    let inserted = false;
    try {
      inserted = document.execCommand("insertText", false, text);
    } catch {
      inserted = false;
    }
    if (!inserted) {
      setDraft(before + text + field.value.slice(field.selectionEnd ?? at));
      requestAnimationFrame(() => field.setSelectionRange(at + text.length, at + text.length));
    }
  };

  const remove = async () => {
    if (!taskId || !window.confirm("Delete this task? This cannot be undone.")) return;
    try {
      await api.deleteTask(taskId);
      onClose();
      onChanged();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "Could not delete the task");
    }
  };

  const dueBounds = useMemo(
    () => ({
      min: board ? toLocalInputValue(board.board.startsAt) : undefined,
      max: board ? toLocalInputValue(board.board.endsAt) : undefined,
    }),
    [board?.board.startsAt, board?.board.endsAt],
  );

  const open = taskId !== null && task !== null && board !== undefined && column !== null;
  const assignee = open ? users.find((user) => user.id === task.assigneeId) : undefined;
  /**
   * The same card-then-board fallback core does, resolved here from data the
   * dialog already has: what matters to the user is the directory the work would
   * happen in, not which of the two rows happens to name it.
   */
  const boardProject = projects.find((project) => project.id === board?.board.projectId) ?? null;
  const taskProject = task?.projectId ? projects.find((project) => project.id === task.projectId) ?? null : null;
  const effectiveProject = taskProject ?? boardProject;
  const overdue =
    open && column.kind !== "done" && task.dueAt !== null && new Date(task.dueAt).getTime() < Date.now();

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Half the page from 1280px up, three quarters below it: wide enough to read
          a thread as a conversation, with the board still in view beside it. */}
      <SheetContent hideClose className="w-full max-w-none sm:w-[75vw] xl:w-1/2" aria-describedby={undefined}>
        {open ? (
          <>
            {/* The window controls get a bar of their own above the scrolling body,
                rather than floating over it: a long description once scrolled
                straight under the X and the pencil. Once the title has scrolled
                away the bar names the card, so a phone deep in a thread still
                says which one is open. */}
            <div
              className={cn(
                "flex shrink-0 items-center gap-1 border-b bg-elevated pl-4 pr-2.5 pt-[max(0.5rem,env(safe-area-inset-top))] pb-1 transition-colors sm:pl-6",
                scrolled ? "border-border/70" : "border-transparent",
              )}
            >
              <p
                aria-hidden
                className={cn(
                  "min-w-0 flex-1 truncate text-[13.5px] font-semibold transition-opacity duration-150",
                  titleGone ? "opacity-100" : "pointer-events-none opacity-0",
                )}
              >
                {task.title}
              </p>
              {canEdit ? (
                <QuickStateButton
                  column={column}
                  columns={board.columns}
                  busy={quickMoving}
                  onMove={(target, action) => void quickStateMove(target.id, action)}
                />
              ) : null}
              <Hint label={editing ? "Done editing" : "Edit this card"} side="bottom">
                <button
                  type="button"
                  onClick={() => setEditing((current) => !current)}
                  aria-label={editing ? "Done editing" : "Edit task"}
                  aria-pressed={editing}
                  className={cn(
                    "rounded-sm p-1.5 transition-[opacity,background-color,color]",
                    editing
                      ? "bg-primary/12 text-primary opacity-100 hover:bg-primary/20"
                      : "text-muted-foreground opacity-70 hover:bg-muted hover:opacity-100",
                  )}
                >
                  {editing ? <Check className="size-4" /> : <Pencil className="size-4" />}
                </button>
              </Hint>
              <SheetClose
                className="rounded-sm p-1.5 text-muted-foreground opacity-70 transition-opacity hover:bg-muted hover:opacity-100"
                aria-label="Close"
              >
                <X className="size-4" />
              </SheetClose>
            </div>

            <div
              ref={threadRef}
              onScroll={(event) => {
                const body = event.currentTarget;
                threadPinnedRef.current = body.scrollHeight - body.scrollTop - body.clientHeight < 80;
                setScrolled(body.scrollTop > 2);
                const title = titleRef.current;
                setTitleGone(title ? title.getBoundingClientRect().bottom < body.getBoundingClientRect().top : false);
              }}
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6 pt-1 scrollbar-slim sm:px-6"
            >
              <DialogHeader className="pr-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  {canEdit ? (
                    <StateMenu column={column} columns={board.columns} onPick={(columnId) => void move(columnId)}>
                      <StateBadgeTrigger column={column} />
                    </StateMenu>
                  ) : (
                    <Badge tint={kindColor(column.kind)}>{column.name}</Badge>
                  )}
                  <Badge tint={priorityColor(task.priority)}>{PRIORITY_LABELS[task.priority]}</Badge>
                  {task.completedAt ? <Badge tint={kindColor("done")}>completed</Badge> : null}
                  {openMentions.length > 0 ? (
                    <Badge tint={MENTION_TINT[openMentions[0]!.status]}>
                      {askLabel(openMentions[0]!)}
                    </Badge>
                  ) : null}
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">{task.id}</span>
                </div>
                <div ref={titleRef}>
                {editing ? (
                  <DialogTitle asChild>
                    <Input
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      onBlur={() => title.trim() && title !== task.title && void patch({ title: title.trim() })}
                      onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
                      className="h-auto border-0 bg-transparent px-0 text-base font-semibold shadow-none focus:shadow-none"
                      aria-label="Task title"
                    />
                  </DialogTitle>
                ) : (
                  <DialogTitle className="text-[17px] font-semibold leading-snug text-balance">
                    {task.title}
                  </DialogTitle>
                )}
                </div>
                <DialogDescription>
                  On <span className="font-medium text-foreground">{board.board.name}</span>
                  {board.board.endsAt ? `, which closes ${formatDateTime(board.board.endsAt)}` : ", which has no deadline"} · created by {task.createdBy === "claude" ? "System" : "you"}{" "}
                  {relativeTime(task.createdAt)}
                </DialogDescription>
                {agents
                  .filter((agent) => agent.status === "working" && agent.currentTaskId === task.id)
                  .map((agent) => (
                    <div
                      key={agent.id}
                      className="mt-0.5 inline-flex max-w-full items-center gap-1.5 self-start rounded-full bg-muted/60 py-0.5 pl-2 pr-0.5 text-[11.5px] text-muted-foreground"
                    >
                      <Loader2 className="size-3 shrink-0 animate-spin" />
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-foreground">{agent.name}</span> is working on this
                      </span>
                      <StopAgentButton
                        agentId={agent.id}
                        agentName={agent.name}
                        working
                        stopping={agent.stopping}
                        queued={agent.pendingTurns}
                        onStopped={onChanged}
                      />
                    </div>
                  ))}
                {agents.flatMap((agent) =>
                  (agent.parallelRuns ?? [])
                    .filter((run) => run.taskId === task.id)
                    .map((run) => (
                      <div
                        key={run.turnId}
                        className="mt-1 flex items-center gap-2 rounded-md border border-border/70 bg-muted/40 px-2.5 py-1.5 text-[12.5px]"
                      >
                        <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{agent.name}</span> is working on this card in parallel
                          {run.branch ? <span className="font-mono text-[11px] text-muted-foreground"> · {run.branch}</span> : null}
                        </span>
                        <RunStopButton
                          agentId={agent.id}
                          turnId={run.turnId}
                          label={`${agent.name}'s run`}
                          stopping={run.stopping}
                          onStopped={onChanged}
                        />
                      </div>
                    )),
                )}
              </DialogHeader>

              {/* Above everything else on the card: it is the one part of it asking
                  the user to do something, and the answer can be given right here. */}
              {waitingCard ? (
                <WaitingBlock
                  card={waitingCard}
                  agents={agents}
                  users={users}
                  handles={handles}
                  now={Date.now()}
                  onOpenResponse={setOpenResponseId}
                  onAnswered={() => {
                    void waiting.refresh();
                    void reloadResponses();
                    onChanged();
                  }}
                  onError={onError}
                  onOpenAgent={onOpenAgent}
                />
              ) : null}

              {editing ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="State">
                    <Select value={column.id} onValueChange={(value) => void move(value)}>
                      <SelectTrigger aria-label="State">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {board.columns.map((entry) => (
                          <SelectItem key={entry.id} value={entry.id}>
                            <span className="inline-flex items-center gap-2">
                              <span className="size-2 rounded-full" style={{ backgroundColor: kindColor(entry.kind) }} />
                              {entry.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Assignee" hint="who does it">
                    <Select
                      value={task.assigneeId ?? UNASSIGNED}
                      onValueChange={(value) => void patch({ assignee: value === UNASSIGNED ? null : value })}
                    >
                      <SelectTrigger aria-label="Assignee">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNASSIGNED}>
                          <span className="inline-flex items-center gap-2 text-muted-foreground">
                            <UserIcon className="size-3.5" /> Unassigned
                          </span>
                        </SelectItem>
                        {/* `claude` is the System actor, never offered as an assignee; a card
                            still held by it keeps showing it, so the select has a value. */}
                        {users
                          .filter((user) => (!user.disabled && user.id !== "claude") || user.id === task?.assigneeId)
                          .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <span className="inline-flex items-center gap-2">
                              <Avatar
                                {...avatarOf(user)}
                                tint={user.id === "claude" ? "var(--primary)" : "var(--kind-active)"}
                                size="sm"
                                foreign={foreign.get(user.id)}
                              />
                              {user.displayName}
                              {user.kind === "agent" && user.id !== "claude" ? (
                                <span className="text-[10px] text-muted-foreground">does the work</span>
                              ) : null}
                            </span>
                          </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Priority">
                    <Select value={task.priority} onValueChange={(value) => void patch({ priority: value as Priority })}>
                      <SelectTrigger aria-label="Priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((priority) => (
                          <SelectItem key={priority} value={priority}>
                            <span className="inline-flex items-center gap-2">
                              <span
                                className="size-2 rounded-full"
                                style={{ backgroundColor: priorityColor(priority) }}
                              />
                              {PRIORITY_LABELS[priority]}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Due" hint="within the board window">
                    <Input
                      type="datetime-local"
                      value={toLocalInputValue(task.dueAt)}
                      min={dueBounds.min}
                      max={dueBounds.max}
                      onChange={(event) => void patch({ dueAt: fromLocalInputValue(event.target.value) })}
                      aria-label="Due date"
                    />
                  </Field>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label hint="where the work happens">Project</Label>
                    <ProjectSelect
                      projects={projects}
                      value={task.projectId}
                      inheritFrom={boardProject}
                      boardProjectIds={boardIds}
                      onChange={(projectId) => void patch({ project: projectId })}
                    />
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      {effectiveProject ? (
                        <>
                          An <span className="font-medium text-primary">@agent</span> request on this card runs inside{" "}
                          <span className="font-mono text-[10.5px] text-foreground">{effectiveProject.hostPath}</span> and can
                          change code there.
                        </>
                      ) : (
                        `With no project, an agent can answer about this card but has no codebase to work in.${
                          boardIds.length > 1 ? " This board covers several: pick the one this card is about." : ""
                        }`
                      )}
                    </p>
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label hint="for parallel runs">Areas</Label>
                    <AreasEditor areas={task.areas ?? []} onChange={(areas) => void patch({ areas })} />
                  </div>
                </div>
              ) : (
                <CollapsibleSection
                  id="details"
                  label="Details"
                  summary={[
                    column.name,
                    assignee?.displayName ?? "Unassigned",
                    PRIORITY_LABELS[task.priority],
                    task.dueAt ? `due ${formatDateTime(task.dueAt)}${overdue ? " (overdue)" : ""}` : "no due date",
                    effectiveProject?.name,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  <dl className="grid gap-x-5 gap-y-3 sm:grid-cols-2">
                    <ReadField label="State">
                      {canEdit ? (
                        <StateMenu column={column} columns={board.columns} onPick={(columnId) => void move(columnId)}>
                          <button
                            type="button"
                            aria-label={`State: ${column.name}. Change state`}
                            className="-mx-1 inline-flex items-center gap-2 rounded-sm px-1 outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/60"
                          >
                            <span className="size-2 rounded-full" style={{ backgroundColor: kindColor(column.kind) }} />
                            {column.name}
                            <ChevronDown className="size-3.5 text-muted-foreground" />
                          </button>
                        </StateMenu>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span className="size-2 rounded-full" style={{ backgroundColor: kindColor(column.kind) }} />
                          {column.name}
                        </span>
                      )}
                    </ReadField>

                    <ReadField label="Assignee">
                      {assignee ? (
                        <span className="inline-flex items-center gap-2">
                          <Avatar
                            {...avatarOf(assignee)}
                            tint={assignee.id === "claude" ? "var(--primary)" : "var(--kind-active)"}
                            size="sm"
                            foreign={foreign.get(assignee.id)}
                          />
                          {assignee.displayName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-muted-foreground">
                          <UserIcon className="size-3.5" /> Unassigned
                        </span>
                      )}
                    </ReadField>

                    <ReadField label="Priority">
                      <span className="inline-flex items-center gap-2">
                        <span className="size-2 rounded-full" style={{ backgroundColor: priorityColor(task.priority) }} />
                        {PRIORITY_LABELS[task.priority]}
                      </span>
                    </ReadField>

                    <ReadField label="Due">
                      {task.dueAt ? (
                        <span className={cn("inline-flex items-center gap-1.5", overdue && "font-medium text-destructive")}>
                          {formatDateTime(task.dueAt)}
                          {overdue ? <span className="text-[11px] uppercase tracking-wide">overdue</span> : null}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">No due date</span>
                      )}
                    </ReadField>

                    <ReadField label="Project">
                      {effectiveProject ? (
                        <Hint
                          label={`${effectiveProject.hostPath} — ${taskProject ? "set on this card" : "inherited from the board"}`}
                        >
                          <span className="inline-flex min-w-0 max-w-full items-center gap-2">
                            <FolderGit2 className="size-3.5 shrink-0" style={{ color: "var(--kind-review)" }} />
                            {/* The name is what a person reads; the path gives way first. */}
                            <span className="shrink-0">{effectiveProject.name}</span>
                            <span className="min-w-0 truncate font-mono text-[10.5px] text-muted-foreground">
                              {shortPath(effectiveProject.path)}
                            </span>
                            {taskProject && !boardIds.includes(taskProject.id) ? (
                              <Badge variant="outline">overrides the board</Badge>
                            ) : null}
                          </span>
                        </Hint>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-muted-foreground">
                          <FolderGit2 className="size-3.5" /> None
                        </span>
                      )}
                    </ReadField>

                    {task.areas?.length ? (
                      <ReadField label="Areas">
                        <AreaChips areas={task.areas} />
                      </ReadField>
                    ) : null}

                    {task.completedAt ? (
                      <ReadField label="Completed">
                        <span className="inline-flex items-center gap-1.5" style={{ color: kindColor("done") }}>
                          <Check className="size-3.5" />
                          {formatDateTime(task.completedAt)}
                        </span>
                      </ReadField>
                    ) : null}
                  </dl>
                </CollapsibleSection>
              )}

              {canvases.length > 0 ? (
                <section className="flex flex-wrap items-center gap-1.5" aria-label="On canvases">
                  <span className="mr-0.5 text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">On canvases</span>
                  {canvases.map((ref) => (
                    <button
                      key={ref.nodeId}
                      type="button"
                      onClick={() => onOpenBoard?.(ref.boardId)}
                      disabled={!onOpenBoard}
                      className="inline-flex max-w-full items-center gap-1 rounded-full border border-border/70 bg-muted/50 px-2 py-0.5 text-[12px] text-foreground transition-colors hover:border-ring/50 hover:bg-muted disabled:cursor-default"
                    >
                      <Network className="size-3 shrink-0 text-muted-foreground" />
                      <span className="truncate">{ref.boardName}</span>
                    </button>
                  ))}
                </section>
              ) : null}

              {/* The description has its own editor rather than riding the card's
                  pencil: it is rendered markdown by default — most are written by
                  agents out of a mail or a chat — and the raw text is one step away. */}
              <section className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <SectionLabel>Description</SectionLabel>
                  {canEdit && !editingDescription && task.description ? (
                    <Hint label="Edit the description" side="left">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={startDescriptionEdit}
                        aria-label="Edit description"
                        className="ml-auto size-6 text-muted-foreground"
                      >
                        <Pencil />
                      </Button>
                    </Hint>
                  ) : null}
                </div>
                {editingDescription ? (
                  <div className="space-y-2">
                    <Textarea
                      value={description}
                      placeholder="What does done look like? Markdown works: ## headings, **bold**, - lists, - [ ] checklists."
                      onChange={(event) => setDescription(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          event.preventDefault();
                          void saveDescription();
                        }
                      }}
                      autoFocus
                      className="min-h-32 font-mono text-[12.5px] leading-relaxed"
                    />
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setEditingDescription(false)} disabled={savingDescription}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => void saveDescription()} loading={savingDescription}>
                        <Check /> Save
                      </Button>
                    </div>
                  </div>
                ) : task.description ? (
                  <div
                    className={cn(
                      "max-w-prose rounded-md border border-border/60 bg-surface/50 p-3",
                      // On a phone the box cost a third of the line; a light rule keeps
                      // it apart from the fields without taking the width.
                      "max-sm:rounded-none max-sm:border-y-0 max-sm:border-r-0 max-sm:border-l-2 max-sm:bg-transparent max-sm:py-0 max-sm:pl-2.5 max-sm:pr-0",
                    )}
                  >
                    <Markdown
                      text={tickedDescription ?? task.description}
                      handles={handles}
                      onToggle={canEdit ? (index) => void toggleCheckbox(index) : undefined}
                      className="text-[13.5px] leading-[1.7] text-card-foreground"
                    />
                  </div>
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={startDescriptionEdit}
                    className="flex w-full items-center gap-2 rounded-md border border-dashed border-border/70 px-3 py-2.5 text-left text-[13px] text-muted-foreground transition-colors hover:border-ring/45 hover:text-foreground"
                  >
                    <Pencil className="size-3.5" /> No description. Add one.
                  </button>
                ) : (
                  <p className="text-[13px] text-muted-foreground">No description.</p>
                )}
              </section>

              {/* Right under the description: on a bug reported in a chat, the
                  screenshot usually is the description. */}
              {photos.length > 0 ? (
                <CollapsibleSection
                  id="photos"
                  label={photos.length === 1 ? "Photo from the chat" : `Photos from the chat · ${photos.length}`}
                  summary={photos.length === 1 ? "1 photo" : `${photos.length} photos`}
                >
                  <PhotoCarousel photos={photos} />
                </CollapsibleSection>
              ) : null}

              {column.kind === "blocked" || task.blockedReason ? (
                editing ? (
                  <Field label="Blocked because" hint="shown on the card">
                    <Input
                      defaultValue={task.blockedReason ?? ""}
                      placeholder="Waiting on…"
                      onBlur={(event) =>
                        event.target.value !== (task.blockedReason ?? "") &&
                        void patch({ blockedReason: event.target.value || null })
                      }
                      className="border-l-2"
                      style={{ borderLeftColor: kindColor("blocked") }}
                    />
                  </Field>
                ) : (
                  <section className="space-y-1.5">
                    <SectionLabel>Blocked because</SectionLabel>
                    <p
                      className="whitespace-pre-wrap break-words border-l-2 pl-3 text-[13.5px] leading-relaxed text-card-foreground"
                      style={{ borderLeftColor: kindColor("blocked") }}
                    >
                      {task.blockedReason ?? "Reason not recorded."}
                    </p>
                  </section>
                )
              ) : null}

              <Separator />

              {/* Above the thread on purpose: on a card that came out of somebody's
                  mail, the reply owed back is the point of the card, and the thread
                  is the conversation *about* it. */}
              <ResponseBoxes
                summary={responses}
                imported={task.sourceRef !== null}
                busy={draftingReplies}
                onOpen={(response) => setOpenResponseId(response.id)}
                onRequestDrafts={() => void requestDrafts()}
                onCancelDrafts={() => void cancelDrafts()}
              />

              <Separator />

              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Thread
                  <span className="rounded-full bg-muted px-1.5 text-[10px]">{comments.length}</span>
                </h3>

                {/* Says out loud that the ask was registered. Without it a highlighted
                    word is the only evidence, which is not evidence. */}
                {openMentions.length > 0 ? (
                  <div
                    className="flex items-start gap-2 rounded-md border px-2.5 py-2 text-[12.5px] leading-relaxed"
                    style={{
                      borderColor: `color-mix(in oklab, ${MENTION_TINT[openMentions[0]!.status]} 35%, transparent)`,
                      backgroundColor: `color-mix(in oklab, ${MENTION_TINT[openMentions[0]!.status]} 8%, transparent)`,
                    }}
                  >
                    {openMentions[0]!.status === "claimed" ? (
                      <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin" style={{ color: MENTION_TINT.claimed }} />
                    ) : (
                      <Sparkles className="mt-0.5 size-3.5 shrink-0" style={{ color: MENTION_TINT.pending }} />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium" style={{ color: MENTION_TINT[openMentions[0]!.status] }}>
                        {openMentions.length === 1
                          ? askLabel(openMentions[0]!)
                          : `${openMentions.length} open requests — ${[...new Set(openMentions.map((ask) => nameOf(ask.targetId)))].join(", ")}`}
                      </p>
                      <p className="text-muted-foreground">
                        {openMentions[0]!.status === "claimed"
                          ? `${nameOf(openMentions[0]!.targetId)} is on it and posts each step in the thread below as it goes.`
                          : openMentions[0]!.targetId === "claude"
                            ? "@claude is not an agent and nothing answers it — mention an agent such as @master."
                            : `${nameOf(openMentions[0]!.targetId)} was woken and picks this up in its own session${
                                agents.find((agent) => agent.id === openMentions[0]!.targetId)?.status === "working"
                                  ? " once its current run finishes"
                                  : ""
                              }.`}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-2">
                  {comments.length === 0 ? (
                    <p className="py-2 text-xs text-muted-foreground">
                      No comments yet. Agents post each step of their work here — type{" "}
                      <span className="font-medium text-primary">@</span> to tag one and ask it for something on this card.
                    </p>
                  ) : (
                    comments.map((comment) => {
                      const author = users.find((user) => user.id === comment.authorId);
                      // Before the user list has loaded (or for an id it lacks),
                      // the id itself says who wrote it, so an agent's comment
                      // does not flash up in a person's colours first.
                      const isAgent = author
                        ? author.kind === "agent"
                        : comment.authorId === "claude" || comment.authorId.startsWith("agt_");
                      const ask = mentionByComment.get(comment.id);
                      const kind = comment.kind === "note" ? null : COMMENT_KIND[comment.kind];
                      const KindIcon = kind?.icon;
                      // A comment that asked for something is left-ruled in its
                      // request's colour, so the thread shows at a glance which
                      // notes were asks and which were just notes. A result rules
                      // itself the same way; a blocker takes the whole card,
                      // because it is the one comment asking the user to act.
                      const rule = ask
                        ? { borderLeftWidth: 2, borderLeftColor: MENTION_TINT[ask.status] }
                        : comment.kind === "blocker" && kind
                          ? {
                              borderLeftWidth: 2,
                              borderLeftColor: kind.tint,
                              borderColor: `color-mix(in oklab, ${kind.tint} 40%, transparent)`,
                              backgroundColor: `color-mix(in oklab, ${kind.tint} 8%, transparent)`,
                            }
                          : comment.kind === "result" && kind
                            ? { borderLeftWidth: 2, borderLeftColor: kind.tint }
                            : undefined;
                      return (
                        <div
                          key={comment.id}
                          className={cn(
                            "min-w-0 rounded-md border p-2.5 text-[13px] leading-relaxed",
                            isAgent ? "border-primary/25 bg-primary/8" : "border-border/70 bg-surface/60",
                            // A step in a long run is the background noise of the
                            // thread; the blocker and the result are the two the
                            // user came to read, so only those keep full weight.
                            comment.kind === "progress" && "border-border/60 bg-surface/50",
                          )}
                          style={rule}
                        >
                          <div className="mb-1 flex items-center gap-2">
                            <Avatar
                              {...(author ? avatarOf(author) : { name: comment.authorId })}
                              tint={isAgent ? "var(--primary)" : "var(--kind-active)"}
                              size="sm"
                              foreign={foreign.get(comment.authorId)}
                            />
                            <span className="text-xs font-medium">{author?.displayName ?? comment.authorId}</span>
                            {kind && KindIcon ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide ring-1 ring-inset"
                                style={{
                                  color: kind.tint,
                                  backgroundColor: `color-mix(in oklab, ${kind.tint} 12%, transparent)`,
                                  // @ts-expect-error CSS custom property for the ring color
                                  "--tw-ring-color": `color-mix(in oklab, ${kind.tint} 30%, transparent)`,
                                }}
                              >
                                <KindIcon className="size-2.5" />
                                {kind.label}
                              </span>
                            ) : null}
                            {ask ? (
                              <Hint
                                label={
                                  ask.resolution
                                    ? `${askLabel(ask)} — ${ask.resolution}`
                                    : askLabel(ask)
                                }
                              >
                                <span
                                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide ring-1 ring-inset"
                                  style={{
                                    color: MENTION_TINT[ask.status],
                                    backgroundColor: `color-mix(in oklab, ${MENTION_TINT[ask.status]} 12%, transparent)`,
                                    // @ts-expect-error CSS custom property for the ring color
                                    "--tw-ring-color": `color-mix(in oklab, ${MENTION_TINT[ask.status]} 30%, transparent)`,
                                  }}
                                >
                                  <AtSign className="size-2.5" />
                                  {askLabel(ask)}
                                </span>
                              </Hint>
                            ) : null}
                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                              <Clock className="size-3" />
                              {relativeTime(comment.createdAt)}
                            </span>
                          </div>
                          {/* Every comment is markdown — the composer says so and
                              previews it, so a person sees how their asterisks
                              render before sending. No onToggle: a box ticked in
                              somebody's comment would rewrite what they said. */}
                          <Markdown text={comment.body} handles={handles} className="text-card-foreground" />
                          {comment.attachments.length > 0 ? (
                            <ImageGroup
                              images={comment.attachments.map((image) => ({ src: api.commentImageUrl(image.id), filename: image.filename }))}
                              className={cn("flex flex-wrap gap-1.5", comment.body && "mt-2")}
                            >
                              {(openImage) => comment.attachments.map((image, position) => (
                                <button
                                  key={image.id}
                                  type="button"
                                  onClick={() => openImage(position)}
                                  title={image.filename}
                                  aria-label={`View ${image.filename}`}
                                  className="block cursor-zoom-in overflow-hidden rounded border border-border/60 bg-surface/60 transition-colors hover:border-ring/45"
                                >
                                  <img
                                    src={api.commentImageUrl(image.id)}
                                    alt={image.filename}
                                    loading="lazy"
                                    className="h-32 max-w-[16rem] object-cover"
                                  />
                                </button>
                              ))}
                            </ImageGroup>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>

              </section>

              <Separator />

              <div className="flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  Updated {relativeTime(task.updatedAt)}
                  {task.blockedReason ? (
                    <span className="ml-2 inline-flex items-center gap-1" style={{ color: kindColor("blocked") }}>
                      <Ban className="size-3" /> blocked
                    </span>
                  ) : null}
                </p>
                {editing ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void remove()}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 /> Delete task
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Pinned under the scrolling body, as a chat's composer is: the thread
                is the conversation every session on this card shares, and answering
                it should not mean scrolling back down to find the box. One rounded
                container holds everything — staged images, the text, the tools and
                Send — the way the agent chat's composer does. */}
            <div className="shrink-0 border-t border-border/70 bg-elevated px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6">
              <div
                className={cn(
                  "rounded-xl border bg-card shadow-sm transition-colors focus-within:border-ring/50 focus-within:ring-2 focus-within:ring-ring/20",
                  dragOver
                    ? "border-dashed border-primary bg-primary/5"
                    : draftAsksClaude
                      ? "border-primary/45"
                      : "border-border",
                )}
                onDragOver={(event) => {
                  if (![...event.dataTransfer.types].includes("Files")) return;
                  event.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(event) => {
                  if (!event.dataTransfer.files.length) return;
                  event.preventDefault();
                  setDragOver(false);
                  addImages(event.dataTransfer.files);
                }}
              >
                {images.length > 0 ? (
                  <ImageGroup
                    images={images.map((entry) => ({ src: entry.preview, filename: entry.file.name || "pasted image" }))}
                    className="flex flex-wrap gap-2 px-3 pt-2.5"
                  >
                    {(openImage) => images.map((entry, position) => (
                      <span key={entry.key} className="relative">
                        <button type="button" onClick={() => openImage(position)} aria-label={`View ${entry.file.name || "image"}`} className="block cursor-zoom-in">
                          <img
                            src={entry.preview}
                            alt={entry.file.name}
                            className="size-12 rounded-md border border-border/70 object-cover"
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => unstageImage(entry.key)}
                          className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground"
                          aria-label={`Remove ${entry.file.name || "image"}`}
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </ImageGroup>
                ) : null}
                {previewing ? (
                  <div
                    // Focusable, so Ctrl/⌘+Enter still sends from here.
                    tabIndex={0}
                    role="region"
                    aria-label="Comment preview"
                    onKeyDown={(event) => {
                      if (isSendKey(event)) {
                        event.preventDefault();
                        void postComment();
                      }
                    }}
                    className="max-h-[30dvh] min-h-10 w-full overflow-y-auto px-3 pb-1 pt-2.5 text-[13.5px] leading-relaxed outline-none scrollbar-slim"
                  >
                    {draft.trim() ? (
                      <Markdown text={draft} handles={handles} className="text-card-foreground" />
                    ) : (
                      <p className="text-muted-foreground">Nothing to preview</p>
                    )}
                  </div>
                ) : (
                  <ComposerTextarea
                    ref={draftRef}
                    value={draft}
                    onSend={() => void postComment()}
                    maxHeight={(height) => height * 0.3}
                    onChange={(event) => setDraft(event.target.value)}
                    onPaste={(event) => {
                      // A screenshot off the clipboard becomes an attachment, not text.
                      const files = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith("image/"));
                      if (files.length > 0) {
                        event.preventDefault();
                        addImages(files);
                      }
                    }}
                    mentions={mentionOptions}
                    placeholder="Add a note… (@ to ask an agent)"
                    className="px-3 pb-1 pt-2.5 text-[13.5px] leading-relaxed"
                    rows={1}
                  />
                )}
                {/* The tools live in the box's foot: icons on a phone, where a row of
                    labels did not fit, and Send where the eye ends. */}
                <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
                  <Hint label="Attach images — or paste or drop them here" side="top">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => imageInput.current?.click()}
                      aria-label="Attach images"
                      className="h-7 px-2 text-muted-foreground hover:text-foreground max-sm:px-1.5"
                    >
                      <ImagePlus /> <span className="max-sm:hidden">Image</span>
                    </Button>
                  </Hint>
                  <Hint label="Tag an agent to ask it for something on this card" side="top">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startMention}
                      aria-label="Tag an agent"
                      className={cn(
                        "h-7 px-2 text-muted-foreground hover:text-foreground max-sm:px-1.5",
                        draftAsksClaude && "bg-primary/12 text-primary hover:bg-primary/20 hover:text-primary",
                      )}
                    >
                      <AtSign /> <span className="max-sm:hidden">Tag</span>
                    </Button>
                  </Hint>
                  <Hint label={previewing ? "Back to writing" : "Preview the markdown"} side="top">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-pressed={previewing}
                      aria-label={previewing ? "Edit the comment" : "Preview the comment"}
                      onClick={() => (previewing ? editDraft() : setPreviewing(true))}
                      className={cn(
                        "h-7 px-2 text-muted-foreground hover:text-foreground max-sm:px-1.5",
                        previewing && "bg-accent text-foreground",
                      )}
                    >
                      {previewing ? <Pencil /> : <Eye />}
                      <span className="max-sm:hidden">{previewing ? "Edit" : "Preview"}</span>
                    </Button>
                  </Hint>
                  <div className="ml-auto flex items-center gap-2">
                  <SendHint className="max-sm:hidden" />
                  <Button
                    onClick={() => void postComment()}
                    loading={posting}
                    disabled={!draft.trim() && images.length === 0}
                    size="icon-sm"
                    aria-label="Send"
                    className={cn(SEND_BUTTON_CLASS, "size-8")}
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                  </div>
                </div>
              </div>
              {/* The one thing worth spelling out: a mention is not just a note. Said
                  always while the draft asks an agent; otherwise only where there is
                  room for a line of help. */}
              <p
                className={cn(
                  "mt-1.5 px-1 text-[11px] transition-colors",
                  draftAsksClaude ? "font-medium text-primary" : "text-muted-foreground max-sm:hidden",
                )}
              >
                {draftAsksClaude
                  ? `Sending this asks ${draftTargets.join(" and ")} to act on it${
                      effectiveProject ? ` — in ${shortPath(effectiveProject.path)} —` : ""
                    } and tracks the request until ${draftTargets.length > 1 ? "they reply" : "it replies"}.`
                  : "Markdown works. Tagging an agent with @ turns a comment into a request it is woken to carry out."}
              </p>
              <input
                ref={imageInput}
                type="file"
                accept={IMAGE_TYPES.join(",")}
                multiple
                className="hidden"
                onChange={(event) => {
                  addImages(event.target.files);
                  event.target.value = "";
                }}
              />
            </div>
          </>
        ) : null}
      </SheetContent>

      {/* A sheet over the dialog rather than a route: the card stays visible
          behind it, which is what makes the message readable in context. */}
      <ResponsePanel
        response={openResponse}
        onClose={() => setOpenResponseId(null)}
        onChanged={() => {
          void reloadResponses();
          onChanged();
        }}
        onError={onError}
      />
    </Sheet>
  );
}

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label hint={hint}>{label}</Label>
    {children}
  </div>
);

/** Read-only counterpart to `Field` — same rhythm, no control. */
const ReadField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <dt className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-[13px] text-card-foreground">{children}</dd>
  </div>
);

/** Whether a collapsible section is open, remembered per section in this browser. */
function useSectionOpen(id: string): [boolean, () => void] {
  const key = `automation.taskDialog.${id}.open`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) !== "false";
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setOpen((current) => {
      try {
        localStorage.setItem(key, String(!current));
      } catch {
        // Private window or blocked storage: it just will not be remembered.
      }
      return !current;
    });
  return [open, toggle];
}

/**
 * A read-only block that folds away. Remembered across cards rather than per
 * card: someone who closes the photos is saying they do not want them in the
 * way, not that this one card's photos were dull. Collapsed, the heading keeps
 * a one-line summary so the section still says what is in it.
 */
function CollapsibleSection({
  id,
  label,
  summary,
  children,
}: {
  id: string;
  label: string;
  summary?: string;
  children: React.ReactNode;
}) {
  const [open, toggle] = useSectionOpen(id);
  return (
    <section className="space-y-1.5">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="group flex w-full min-w-0 items-center gap-1.5 text-left"
      >
        <ChevronRight
          className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
        />
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground group-hover:text-foreground">
          {label}
        </span>
        {!open && summary ? (
          <span className="min-w-0 truncate text-[12px] text-muted-foreground">· {summary}</span>
        ) : null}
      </button>
      {open ? children : null}
    </section>
  );
}

/** Heading for a read-only block — `Label` styling without a control to point at. */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</p>
);
